import { useRef, } from "react";
import { InducteeInfoFragment, TheirInfluenceFragment } from '@graphqlTypes';
import { getHighlightMarkup } from "@/core/utils";

import { Container, RevealWrapper, RhLink } from '@components';
import Signature from "./Signature";
import { useDebug } from '@/utils/useDebugLog';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView, motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from './InducteeMembers.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

interface InducteeMembersProps {
  content: TheirInfluenceFragment['inducteeData']['membersSection'];
  members: InducteeInfoFragment['inducteeMembers'];
}

const Member = ({ member, single }: { member: InducteeInfoFragment['inducteeMembers'][0], single: boolean }) => {
  const hasLink = !!member?.link;
  const Tag = hasLink ? RhLink : 'div';
  return (
    <Tag
      className={cx(["member", { single, 'has-link': hasLink }])}
      href={member.link || null}
    >
      <Signature content={member?.signature} />
      <span className={cx(["text-m", "name"])}>{member.name}</span>
    </Tag>
  )
}

export default function InducteeMembers({ content, members }: InducteeMembersProps) {

  const { log } = useDebug({ name: 'InducteeMembers', bgColor: 'MediumAquamarine', enabled: false });
  log({ content, members });

  const headline = getHighlightMarkup(content.headline);

  // -------------------- Loaded?

  // Not sure if needed. -Gabe
  const signatureLoaded = () => {
    // log('signatureLoaded');
  };

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Render

  return (
    <motion.section className={cx(["inductee-members"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        <Container>
          <div className={cx(["inductee-members-content"])}>

            <div className={cx(["inductee-members-content-side", "copy"])}>
              <h2 className={cx(["title-uc-l", "headline"])} dangerouslySetInnerHTML={{ __html: headline }} />
              <p className={cx(["text-m", "body"])}>{content.body}</p>
            </div>

            <div className={cx(["inductee-members-content-side", "members"])}>
              {members?.map((member, index) => (
                <Member key={index} member={member} single={members.length === 1} />
              ))}
            </div>

          </div>
        </Container>
      </RevealWrapper>
    </motion.section>
  );

}
